<template>
    <div class="category-property-select">
        <!-- 递归渲染属性树 -->
        <div
            v-for="item in properties"
            :key="`${item.code}-${item.level}`"
            class="property-item"
        >
            <!-- 属性标签 -->
            <div class="property-label">
                <span
                    v-if="item.isRequired === 1"
                    class="required-mark"
                >*</span>
                <span>{{ item.name }}</span>
            </div>

            <!-- 属性输入控件 -->
            <div class="property-input">
                <!-- 查看模式：显示已选值 -->
                <template v-if="isDisabled">
                    <span v-if="item.valueList && item.valueList.length > 0">
                        {{ item.valueList.map(v => v.value).join(', ') }}
                    </span>
                    <span
                        v-else
                        style="color: #999;"
                    >-</span>
                </template>

                <!-- 编辑模式：显示输入控件 -->
                <template v-else>
                    <!-- 可选择类型（1, 2, 4, 5, 7）- 根据是否有级联数据选择组件 -->
                    <mc-cascader
                        v-if="isSelectableType(item.inputType) && hasCascadeData(item)"
                        v-model="item.bindValue"
                        :data="buildCascadeOptions(item)"
                        :disabled="isDisabled"
                        :multiple="isMultipleType(item.inputType)"
                        :allow-create="isCreatableType(item.inputType)"
                        clearable
                        filterable
                        :placeholder="`请输入${item.name}`"
                        @change="(val) => handleCascaderChange(item, val)"
                    />
                    <mc-select
                        v-else-if="isSelectableType(item.inputType)"
                        v-model="item.bindValue"
                        :source="parseSelectValues(item)"
                        :disabled="isDisabled"
                        :multiple="isMultipleType(item.inputType)"
                        :allow-create="isCreatableType(item.inputType)"
                        clearable
                        filterable
                        :placeholder="`请输入${item.name}`"
                        @change="(val) => handlePropertyChange(item, val)"
                    />

                    <!-- 3: 输入 -->
                    <mc-input
                        v-else-if="item.inputType === 3"
                        v-model="item.bindValue"
                        :disabled="isDisabled"
                        :placeholder="`请输入${item.name}`"
                        clearable
                        @input="(val) => handlePropertyChange(item, val)"
                    />

                    <!-- 6: 输入可添加 -->
                    <mc-input
                        v-else-if="item.inputType === 6"
                        v-model="item.bindValue"
                        :disabled="isDisabled"
                        :placeholder="`请输入${item.name}`"
                        clearable
                        @input="(val) => handlePropertyChange(item, val)"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WmCategoryProperty',

    props: {
        // 属性列表
        properties: {
            type: Array,
            default: () => [],
        },
        // 是否禁用
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['change', 'input'],

    computed: {
        isDisabled() {
            return this.disabled;
        },
    },

    watch: {
        properties: {
            handler(newVal) {
                if (newVal) {
                    const traverse = (items) => {
                        items.forEach((item) => {
                            this.initBindValue(item);
                            if (item.childJson && item.childJson.length > 0) {
                                traverse(item.childJson);
                            }
                        });
                    };
                    traverse(newVal);
                }
            },
            immediate: true,
        },
    },

    methods: {
        // 解析选择值（childJson 是选择值列表）
        parseSelectValues(item) {
            if (!item.childJson || item.childJson.length === 0) {
                return [];
            }
            return item.childJson.map((child) => ({
                label: child.name,
                value: child.code,
            }));
        },

        // 检查是否是可选择类型（1, 2, 4, 5, 7）
        isSelectableType(inputType) {
            return [1, 2, 4, 5, 7].includes(inputType);
        },

        // 检查是否是多选类型（2, 5）
        isMultipleType(inputType) {
            return [2, 5].includes(inputType);
        },

        // 检查是否是可创建类型（4, 5, 7）
        isCreatableType(inputType) {
            return [4, 5, 7].includes(inputType);
        },

        // 检查是否有级联数据
        hasCascadeData(item) {
            if (!item.childJson || item.childJson.length === 0) return false;
            // 检查是否有任何选项不是叶子节点
            return item.childJson.some((child) => child.isLeaf !== 1);
        },

        // 构建级联选择器的选项
        buildCascadeOptions(item) {
            // 辅助递归函数
            const traverse = (nodes) => {
                if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return [];

                return nodes.map((node) => {
                    const option = {
                        label: node.name,
                        value: node.code,
                    };

                    if (node.isLeaf !== 1 && node.childJson && node.childJson.length > 0) {
                        const children = traverse(node.childJson);
                        if (children.length > 0) {
                            option.children = children;
                        }
                    }
                    return option;
                });
            };

            return traverse(item.childJson);
        },

        // 辅助函数：根据 code 查找 name
        findNameByCode(list, code) {
            if (!list?.length) return '';
            let res = '';
            list.some((item) => {
                if (item.code === code) {
                    res = item.name;
                    return true;
                }
                if (item.childJson?.length) {
                    const name = this.findNameByCode(item.childJson, code);
                    if (name) {
                        res = name;
                        return true;
                    }
                }
                return false;
            });
            return res;
        },

        // 辅助函数：根据 code 查找路径
        findPathByCode(list, code, path = []) {
            let res = null;
            list.some((item) => {
                const currentPath = [...path, item.code];
                if (item.code === code) {
                    res = currentPath;
                    return true;
                }
                if (item.childJson && item.childJson.length > 0) {
                    const foundPath = this.findPathByCode(item.childJson, code, currentPath);
                    if (foundPath) {
                        res = foundPath;
                        return true;
                    }
                }
                return false;
            });
            return res;
        },

        // 初始化绑定值
        initBindValue(item) {
            // 如果 bindValue 已经存在且不为空，跳过（避免覆盖）
            if (item.bindValue !== undefined) return;

            // 优先使用 valueList
            if (item.valueList && item.valueList.length > 0) {
                if ([1, 2, 4, 5, 7].includes(item.inputType)) {
                    // 级联选择器
                    if (this.hasCascadeData(item)) {
                        if (this.isMultipleType(item.inputType)) {
                            // 多选级联：[[path1], [path2]]
                            item.bindValue = item.valueList.map((v) => this.findPathByCode(item.childJson, v.valueId)).filter(Boolean);
                        } else {
                            // 单选级联：[path]
                            item.bindValue = this.findPathByCode(item.childJson, item.valueList[0].valueId);
                        }
                    } else {
                        // 普通 Select
                        if (this.isMultipleType(item.inputType)) {
                            item.bindValue = item.valueList.map((v) => v.valueId);
                        } else {
                            item.bindValue = item.valueList[0].valueId;
                        }
                    }
                } else {
                    // 输入类型
                    item.bindValue = item.valueList[0].value;
                }
            }
        },

        // 处理级联选择器变化
        handleCascaderChange(item, val) {
            console.log('handleCascaderChange', item.name, val);
            item.bindValue = val; // 更新绑定值

            const newValueList = [];

            if (val && val.length > 0) {
                if (this.isMultipleType(item.inputType)) {
                    // 多选级联，val 是二维数组 [[1, 2], [3, 4]]
                    val.forEach((path) => {
                        const id = path[path.length - 1]; // 取叶子节点 ID
                        const name = this.findNameByCode(item.childJson, id);
                        newValueList.push({ value: name, valueId: id });
                    });
                } else {
                    // 单选级联，val 是一维数组 [1, 2]
                    const id = val[val.length - 1]; // 取叶子节点 ID
                    const name = this.findNameByCode(item.childJson, id);
                    newValueList.push({ value: name, valueId: id });
                }
            }

            item.valueList = newValueList;
            console.log('handleCascaderChange valueList', item.valueList);

            // 清理旧字段
            delete item.value;
            delete item.valueId;

            this.$emit('change', item);
            this.$emit('input', item);
        },

        // 处理属性变化
        handlePropertyChange(item, val) {
            console.log('handlePropertyChange', item.name, val);
            item.bindValue = val; // 更新绑定值

            const newValueList = [];

            if ([1, 2, 4, 5, 7].includes(item.inputType)) {
                // Select (非级联)
                if (val !== undefined && val !== null && val !== '') {
                    if (Array.isArray(val)) {
                        // 多选
                        val.forEach((id) => {
                            const name = this.findNameByCode(item.childJson, id);
                            newValueList.push({ value: name, valueId: id });
                        });
                    } else {
                        // 单选
                        const name = this.findNameByCode(item.childJson, val);
                        newValueList.push({ value: name, valueId: val });
                    }
                }
            } else {
                // Input
                if (val !== undefined && val !== null && val !== '') {
                    newValueList.push({ value: val, valueId: null });
                }
            }

            item.valueList = newValueList;
            console.log('handlePropertyChange valueList', item.valueList);

            // 清理旧字段
            delete item.value;
            delete item.valueId;

            this.$emit('change', item);
            this.$emit('input', item);
        },

        // 验证必填属性
        validate() {
            const errors = [];

            const checkRequired = (items) => {
                items.forEach((item) => {
                    // 检查 valueList 是否有值
                    const hasValue = item.valueList && item.valueList.length > 0;
                    console.log('validate item', item.name, item.isRequired, item.valueList, hasValue);
                    if (item.isRequired === 1 && !hasValue) {
                        errors.push(`${item.name}为必填项`);
                    }
                });
            };

            checkRequired(this.properties);
            return errors.length === 0 ? true : errors;
        },
    },
};
</script>

<style lang="scss" scoped>
.category-property-select {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    margin: 0 -10px;
}

.property-item {
    width: 100%;
    padding: 0 10px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    color: #676767;

    .property-label {
        width: 120px;
        padding-right: 10px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        line-height: 32px; // 与 Input 高度一致
        margin-bottom: 0;
        flex-shrink: 0;

        .required-mark {
            color: #f56c6c;
            margin-right: 4px;
            font-weight: bold;
        }

        .key-property-badge {
            display: inline-block;
            margin-left: 8px;
            padding: 2px 8px;
            background-color: #fff3cd;
            color: #856404;
            border-radius: 2px;
            font-size: 12px;
            transform: scale(0.8); // 缩小一点
        }
    }

    .property-input {
        flex: 1;
        width: 0; // 防止 flex 子项溢出

        ::v-deep .mc-select,
        ::v-deep .mc-input,
        ::v-deep .mc-cascader {
            width: 100%;
            max-width: 100%;
        }

        .input-with-select {
            width: 100%;
        }
    }

    .cascaded-properties {
        margin-top: 12px;
        padding-left: 12px;
        border-left: 2px solid #e0e0e0;

        .cascaded-label {
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
            font-weight: 500;
        }
    }
}
</style>
